// Levels in The hen yard. The id is fixed (progress is saved under it) – never change it once a level is published.
import { defineLevels } from '../../core/registry';
import type { Ball, LevelData, PlacementData, StaticData } from '../../core/types';
import { on } from '../../core/level-helpers';
import { DEG } from './shared';

/** The seesaw fields cup() reads (length, angle in degrees, board offset from the pivot). */
interface SeesawSpec { x: number; y: number; a?: number; len?: number; off?: number }

/** Egg in the seesaw's cup: side -1 = left end, +1 = right end. */
function cup(v: SeesawSpec, sd: number, r?: number): Ball {
  r = r || 15; const L = v.len || 240, a = (v.a || 0) * DEG, lx = (v.off || 0) + sd * (L / 2 - 10 - r - 1), ly = -7 - r - 1;
  return { x: Math.round(v.x + lx * Math.cos(a) - ly * Math.sin(a)), y: Math.round(v.y + lx * Math.sin(a) + ly * Math.cos(a)), r };
}


const GROUND: StaticData = { x: 600, y: 775, w: 1240, h: 50, skin: 'henhouse_ground' };
const L: LevelData[] = [];
/** The standard seesaw on the left, tilted down to the left (egg in the left cup). */
const V0 = (): PlacementData => ({ t: 'henhouse_seesaw', x: 330, y: 690, a: -20 });
/** Hen house wall from the ground (750) up to top. */
const HOUSE = (x: number, top: number, w?: number): StaticData => ({ x, y: (top + 750) / 2, w: w || 60, h: 750 - top, skin: 'henhouse_house' });
/** Straw bale from top down to bot (default: the ground). */
const BALE = (x: number, top: number, w: number, bot?: number): StaticData => ({ x, y: (top + (bot || 750)) / 2, w, h: (bot || 750) - top, skin: 'henhouse_straw' });

{ // 1 – Thump – intro: seesaw (a sack lands on it, the egg flies)
  const v = V0();
  L.push({
    id: 'thump', par: 1, tools: { henhouse_sack: 1 }, learn: ['lever', 'gravity'], intro: 'henhouse_seesaw',
    statics: [GROUND, HOUSE(740, 330)],
    fixed: [v], balls: [cup(v, -1)],
    goal: { type: 'bowl', x: 580, y: 744, w: 230 },
    objs: [{ t: 'duck', x: 548, y: 415 }],
    solution: [{ t: 'henhouse_sack', x: 400, y: 480 }],
  });
}
{ // 2 – Feather – intro: feather (sack or feather? the feather is too light)
  const v = V0();
  L.push({
    id: 'feather', par: 1, tools: { henhouse_feather: 1, henhouse_sack: 1 }, learn: ['gravity', 'weight'], intro: 'henhouse_feather',
    statics: [GROUND, BALE(590, 660, 220), HOUSE(740, 300)],
    fixed: [v], balls: [cup(v, -1)],
    goal: { type: 'bowl', x: 590, y: 653, w: 200 },
    objs: [{ t: 'duck', x: 445, y: 448 }],
    solution: [{ t: 'henhouse_sack', x: 400, y: 520 }],
    fails: [[{ t: 'henhouse_feather', x: 420, y: 480 }]],
  });
}
{ // 3 – Hen – intro: hen (she sits down: a gentle hop)
  const v = V0();
  L.push({
    id: 'hen', par: 1, tools: { henhouse_hen: 1, henhouse_feather: 1 }, learn: ['gravity', 'lever'], intro: 'henhouse_hen',
    statics: [GROUND, HOUSE(700, 520)],
    fixed: [v], balls: [cup(v, -1)],
    goal: { type: 'bowl', x: 560, y: 744, w: 200 },
    objs: [{ t: 'duck', x: 390, y: 528 }],
    solution: [{ t: 'henhouse_hen', x: 420, y: 400 }],
    fails: [[{ t: 'henhouse_feather', x: 420, y: 400 }]],
  });
}
{ // 4 – Far out – lever arm: far out = high up
  const v = V0();
  L.push({
    id: 'far-out', par: 1, tools: { henhouse_sack: 1 }, learn: ['lever', 'henhouse_arm'],
    statics: [GROUND, BALE(720, 640, 230), BALE(720, 552, 200, 640), HOUSE(870, 250)],
    fixed: [v], balls: [cup(v, -1)],
    goal: { type: 'bowl', x: 720, y: 545, w: 200 },
    objs: [{ t: 'duck', x: 655, y: 442 }],
    solution: [{ t: 'henhouse_sack', x: 440, y: 480 }],
    fails: [[{ t: 'henhouse_sack', x: 380, y: 480 }], [{ t: 'henhouse_sack', x: 400, y: 480 }]],
  });
}
{ // 5 – The middle – lever arm: close to the middle = a small hop
  const v = V0();
  L.push({
    id: 'middle', par: 1, tools: { henhouse_sack: 1 }, learn: ['lever', 'henhouse_arm'],
    statics: [GROUND],
    fixed: [v], balls: [cup(v, -1)],
    goal: { type: 'bowl', x: 540, y: 744, w: 190 },
    objs: [{ t: 'duck', x: 345, y: 520 }],
    solution: [{ t: 'henhouse_sack', x: 368, y: 480 }],
    fails: [[{ t: 'henhouse_sack', x: 440, y: 480 }]],
  });
}
{ // 6 – Heavy enough – weight: who is heavy enough? (hen and feather are decoys)
  const v = V0();
  L.push({
    id: 'heavy-enough', par: 1, tools: { henhouse_hen: 1, henhouse_sack: 1, henhouse_feather: 1 }, learn: ['gravity', 'weight'],
    statics: [GROUND, BALE(650, 560, 200), HOUSE(790, 250)],
    fixed: [v], balls: [cup(v, -1)],
    goal: { type: 'bowl', x: 650, y: 553, w: 180 },
    objs: [{ t: 'duck', x: 605, y: 418 }],
    solution: [{ t: 'henhouse_sack', x: 420, y: 480 }],
    fails: [[{ t: 'henhouse_hen', x: 440, y: 480 }], [{ t: 'henhouse_hen', x: 410, y: 300 }], [{ t: 'henhouse_feather', x: 430, y: 480 }]],
  });
}
{ // 7 – Place the seesaw – place the seesaw yourself: the egg and the sack fall down (the plank is a decoy)
  L.push({
    id: 'place-the-seesaw', par: 1, tools: { henhouse_seesaw: 1, plank: 1 }, learn: ['lever', 'mechanism'],
    statics: [GROUND, HOUSE(880, 330)],
    fixed: [{ t: 'henhouse_sack', x: 470, y: 90 }],
    balls: [{ x: 300, y: 590, r: 15 }],
    goal: { type: 'bowl', x: 720, y: 744, w: 230 },
    objs: [{ t: 'henhouse_perch', x: 300, y: 590 }, { t: 'duck', x: 130, y: 640 }],
    solution: [{ t: 'henhouse_seesaw', x: 380, y: 690, a: -20 }],
    fails: [[{ t: 'henhouse_seesaw', x: 380, y: 690, a: 20 }], [{ t: 'plank', x: 380, y: 690, a: 15 }]],
  });
}
{ // 8 – Flip the seesaw – direction: heavy goes down at one end, light goes up at the other
  L.push({
    id: 'flip-the-seesaw', par: 1, tools: { henhouse_seesaw: 1, plank: 1 }, learn: ['lever', 'direction'],
    statics: [GROUND, BALE(340, 650, 250), HOUSE(188, 300, 50)],
    fixed: [{ t: 'henhouse_sack', x: 620, y: 70 }],
    balls: [{ x: 800, y: 610, r: 15 }],
    goal: { type: 'bowl', x: 335, y: 643, w: 230 },
    objs: [{ t: 'henhouse_perch', x: 800, y: 610, dir: -1 }, { t: 'duck', x: 560, y: 380 }],
    solution: [{ t: 'henhouse_seesaw', x: 715, y: 690, a: 20 }],
    fails: [[{ t: 'henhouse_seesaw', x: 715, y: 690, a: -20 }]],
  });
}
{ // 9 – Over the fence – lever arm: not too weak, not too strong
  const v = V0();
  L.push({
    id: 'over-the-fence', par: 1, tools: { henhouse_sack: 1, henhouse_hen: 1, henhouse_feather: 1 }, learn: ['lever', 'henhouse_arm'],
    statics: [GROUND, { x: 490, y: 670, w: 24, h: 160, skin: 'henhouse_post' }],
    fixed: [v], balls: [cup(v, -1)],
    goal: { type: 'bowl', x: 630, y: 744, w: 230 },
    objs: [{ t: 'duck', x: 430, y: 465 }],
    solution: [{ t: 'henhouse_sack', x: 388, y: 480 }],
    fails: [[{ t: 'henhouse_sack', x: 445, y: 480 }], [{ t: 'henhouse_sack', x: 355, y: 480 }], [{ t: 'henhouse_feather', x: 400, y: 480 }]],
  });
}
{ // 10 – Two eggs – counting: the heavy sack for the long trip, the hen for the short one
  const a = V0(), b: PlacementData = { t: 'henhouse_seesaw', x: 900, y: 690, a: 20 };
  L.push({
    id: 'two-eggs', par: 2, tools: { henhouse_sack: 1, henhouse_hen: 1, henhouse_feather: 1 }, learn: ['gravity', 'lever', 'counting'],
    statics: [GROUND],
    fixed: [a, b], balls: [cup(a, -1), cup(b, 1)],
    goal: { type: 'bowl', x: 545, y: 744, w: 280, count: 2 },
    objs: [{ t: 'duck', x: 760, y: 420 }],
    solution: [{ t: 'henhouse_hen', x: 430, y: 420 }, { t: 'henhouse_sack', x: 832, y: 420 }],
    fails: [[{ t: 'henhouse_sack', x: 430, y: 420 }, { t: 'henhouse_hen', x: 880, y: 420 }], [{ t: 'henhouse_hen', x: 430, y: 420 }, { t: 'henhouse_feather', x: 880, y: 420 }]],
  });
}
{ // 11 – Flip and move – direction + lever arm: the right end down, and the sack far out
  L.push({
    id: 'flip-and-move', par: 1, tools: { henhouse_seesaw: 1, henhouse_feather: 1 }, learn: ['lever', 'henhouse_arm', 'direction'],
    statics: [GROUND, BALE(320, 640, 230), BALE(320, 552, 200, 640), HOUSE(170, 250)],
    fixed: [{ t: 'henhouse_sack', x: 610, y: 70 }],
    balls: [{ x: 770, y: 610, r: 15 }],
    goal: { type: 'bowl', x: 320, y: 545, w: 200 },
    objs: [{ t: 'henhouse_perch', x: 770, y: 610, dir: -1 }, { t: 'duck', x: 980, y: 640 }],
    solution: [{ t: 'henhouse_seesaw', x: 715, y: 690, a: 20 }],
    fails: [[{ t: 'henhouse_seesaw', x: 665, y: 690, a: 20 }], [{ t: 'henhouse_seesaw', x: 715, y: 690, a: -20 }]],
  });
}
{ // 12 – Under the roof – ramp: the sack can't fall straight down – let it slide down a plank
  const v = V0();
  L.push({
    id: 'under-the-roof', par: 2, tools: { henhouse_sack: 1, plank: 1, henhouse_feather: 1 }, learn: ['lever', 'ramp', 'gravity'],
    statics: [GROUND, HOUSE(900, 300)],
    fixed: [v], balls: [cup(v, -1)],
    goal: { type: 'bowl', x: 740, y: 744, w: 240 },
    objs: [{ t: 'henhouse_roof', x: 310, y: 400, w: 320 }, { t: 'duck', x: 640, y: 300 }],
    solution: [{ t: 'plank', x: 560, y: 510, a: -15 }, { t: 'henhouse_sack', x: 600, y: 420 }],
    fails: [[{ t: 'henhouse_sack', x: 505, y: 420 }], [{ t: 'plank', x: 560, y: 510, a: -15 }, { t: 'henhouse_feather', x: 600, y: 440 }]],
  });
}
{ // 13 – The egg box – ramp: a plank catches the egg and rolls it over the fence (the goal is an egg tray)
  const v = V0();
  L.push({
    id: 'egg-tray', par: 2, tools: { henhouse_sack: 1, plank: 1, henhouse_hen: 1 }, learn: ['lever', 'ramp'],
    statics: [GROUND, { x: 720, y: 615, w: 30, h: 270, skin: 'henhouse_post' }, HOUSE(1060, 300)],
    fixed: [v], balls: [cup(v, -1)],
    goal: { type: 'bowl', x: 890, y: 744, w: 220, style: 'tray' },
    objs: [{ t: 'duck', x: 900, y: 420 }],
    solution: [{ t: 'henhouse_sack', x: 440, y: 420 }, { t: 'plank', x: 640, y: 470, a: 15 }],
    fails: [[{ t: 'henhouse_sack', x: 440, y: 420 }], [{ t: 'henhouse_hen', x: 440, y: 420 }, { t: 'plank', x: 640, y: 470, a: 15 }]],
  });
}
{ // 14 – The pumpkin – chain reaction: the chick presses the button, the pumpkin falls onto the seesaw
  const v = V0(), sh: StaticData = { x: 130, y: 140, w: 200, h: 20, a: 8, skin: 'henhouse_fence' };
  L.push({
    id: 'pumpkin', par: 2, tools: { plank: 3, box: 1 }, learn: ['chain_reaction', 'ramp', 'lever'],
    statics: [GROUND, sh, { x: 720, y: 300, w: 190, h: 20, skin: 'henhouse_fence' }, BALE(760, 640, 230), BALE(760, 552, 200, 640), HOUSE(905, 250)],
    fixed: [v], balls: [cup(v, -1), on(sh, -70, 13, { helper: true })],
    goal: { type: 'bowl', x: 760, y: 545, w: 200 },
    objs: [{ t: 'button', x: 700, y: 282, act: { cut: 'r1' } }, { t: 'rope', id: 'r1', ax: 440, ay: 40, hang: { t: 'stone', x: 440, y: 350 } }, { t: 'duck', x: 560, y: 560 }],
    solution: [{ t: 'plank', x: 320, y: 192, a: 15 }, { t: 'plank', x: 485, y: 238, a: 15 }],
    fails: [[{ t: 'plank', x: 320, y: 192, a: 15 }]],
  });
}
{ // 15 – Master – everything: two eggs in the same nest (one from the fixed seesaw, one you launch yourself)
  const v: PlacementData = { t: 'henhouse_seesaw', x: 290, y: 690, a: -20 };
  L.push({
    id: 'master', par: 3, tools: { henhouse_seesaw: 1, henhouse_sack: 2, henhouse_hen: 1, henhouse_feather: 1, plank: 1 }, learn: ['lever', 'gravity', 'henhouse_arm', 'counting'],
    statics: [GROUND, BALE(580, 650, 200)],
    fixed: [v], balls: [cup(v, -1), { x: 880, y: 600, r: 15 }],
    goal: { type: 'bowl', x: 580, y: 643, w: 190, count: 2 },
    objs: [{ t: 'henhouse_perch', x: 880, y: 600, dir: -1 }, { t: 'duck', x: 580, y: 330 }],
    solution: [{ t: 'henhouse_sack', x: 370, y: 420 }, { t: 'henhouse_seesaw', x: 800, y: 690, a: 20 }, { t: 'henhouse_sack', x: 745, y: 320 }],
    fails: [[{ t: 'henhouse_sack', x: 370, y: 420 }], [{ t: 'henhouse_feather', x: 370, y: 420 }, { t: 'henhouse_seesaw', x: 800, y: 690, a: 20 }, { t: 'henhouse_sack', x: 745, y: 320 }]],
  });
}

export const levels = defineLevels(L);
